// Midnight — calm neutral charcoal greys (no blue tint), soft off-white
// text rather than pure white, gently rounded corners, and a quiet WHITE
// accent — selected states, primary buttons and focus are soft white /
// light-grey, keeping the chrome monochrome with a white primary button.
// Flat material — no frosted glass, almost no shadows; elevation comes
// from clean stepped greys. Clean neutral grotesk type (Inter) at a
// comfortable reading size with plain, un-tracked section labels. Colour lives in the agent avatars — a vivid, curated identity
// set against the restrained chrome — plus the usual semantic status hues.
//
// The quiet/neutral member of the set (Glass = technical, Earth =
// editorial, Midnight = calm/conversational).

import type { ThemeDefinition } from "./types";
import { FONTS, makeTypography } from "./shared";

const NO_SHADOW = "0 0 0 0 transparent";

export const midnight: ThemeDefinition = {
  name: "midnight",
  label: "Midnight",
  description: "Neutral charcoal greys with a quiet white accent.",
  mode: "dark",
  browserChrome: "#171717",

  // Neutral app type: a clean grotesk (Inter), medium display weight,
  // and plain sentence-case labels rendered as typed (no tracked allcaps)
  // — understated and conversational.
  typography: makeTypography({
    fonts: { sans: FONTS.inter, display: FONTS.inter },
    bodyWeight: 400,
    label: { case: "none", tracking: "0", font: "sans", weight: 500 },
    display: { weight: 600, letterSpacing: "-0.01em" },
    // The accent is white, a step from the #ececec body text: links get
    // a quiet underline so colour is not their only cue (index.css).
    linkUnderline: true,
  }),
  // Soft, generously rounded — panels, composer and chips are all clearly
  // rounded rectangles.
  radius: { sm: "3px", md: "5px", lg: "6px", xl: "6px", xxl: "8px" },

  color: {
    ink: "236, 236, 236", // soft off-white ink (not pure #fff)
    inkSpace: "236 236 236",
    accentRgb: "236, 236, 236", // soft white — the accent channel

    // Darker app frame with panels a clear, even step lighter — a #171717
    // shell with #212121 / #2a2a2a surfaces on top.
    bg: "#171717",
    surface0: "#171717",
    surface1: "#212121",
    surface2: "#2a2a2a",
    surface3: "#343434",

    textPrimary: "#ececec",
    // muted rises with faint so the two levels stay distinct; faint and
    // ghost clear AA (>= 4.5:1) on bg, panel and raised, and on the
    // selected fill over bg, panel and tile.
    textMuted: "rgba(236, 236, 236, 0.74)",
    textFaint: "rgba(236, 236, 236, 0.65)",
    textGhost: "rgba(236, 236, 236, 0.64)",

    // White accent. Solid accent fills (primary buttons, the check dot)
    // pair with `onAccent` dark text (a white button with dark text);
    // translucent accent tints are just light over the dark.
    accent: "#ececec",
    accentLight: "#ffffff",
    accentDark: "#bdbdbd",
    accentDeeper: "#3a3a3a",
    onAccent: "#171717", // dark text/icons on the white accent

    success: "#19c37d", // mint — also the "live" dot
    warning: "#e0a23c", // amber
    danger: "#f58888", // red, AA as text on raised-over-tile and its /15 tint
    info: "#9ab8ff",
    dangerFill: "#c43a3a",
    focus: "#c4c4c4",

    // flat-solid: opaque neutral greys, stepped for clear elevation.
    // Hover / raised sits well below selected (activeAccent) so a hovered
    // row never looks like a second selected one (they were 4/255 apart).
    raised: "rgba(236, 236, 236, 0.055)",
    glassPanel: "#212121",
    glassTile: "#2a2a2a",
    glassKpi: "#262626",
    glassInset: "#161616",
    glassButton: "rgba(236, 236, 236, 0.05)",
    glassButtonHover: "rgba(236, 236, 236, 0.1)",

    // Selected/active state = a soft light fill (about #3c3c3c over a
    // panel), not a coloured highlight; #ececec on it is 9:1.
    activeAccent: "rgba(255, 255, 255, 0.12)",
    activeAccentBorder: "rgba(255, 255, 255, 0.22)",
    // soft enough that #ececec text on it still reads (7.6:1)
    activeAccentStrong: "rgba(255, 255, 255, 0.18)",
    // web only: input/secondary-button edge, selected-row fill
    controlEdge: "rgba(236, 236, 236, 0.22)",

    avatarBg: "rgba(255, 255, 255, 0.1)",
    avatarBorder: "rgba(255, 255, 255, 0.18)",
    avatarText: "#ececec",
    sidebarBg: "rgba(0, 0, 0, 0.22)",
    sidebarEdge: "rgba(236, 236, 236, 0.07)",
    brandBadge: "#ececec",
    brandBadgeMuted: "#212121",
    canvasGrid: "#2f2f2f",

    selectionBg: "rgba(236, 236, 236, 0.85)",
    selectionText: "#171717",
    scrim: "rgba(0, 0, 0, 0.6)",
    modalPanel: "#2a2a2a",
    overlay: "#262626",
    tabbarBg: "#1f1f1f",
  },

  contrast: {
    // Low-contrast, soft dividers — surfaces separate with very faint
    // hairlines, not crisp bright borders. Flat (no bevels).
    border: {
      // 0.08: a panel card still shows its edge where the ground is
      // lightest (the top of the screen).
      hairline: "rgba(236, 236, 236, 0.08)",
      subtle: "rgba(236, 236, 236, 0.1)",
      visible: "rgba(236, 236, 236, 0.16)",
      strong: "rgba(236, 236, 236, 0.24)",
    },
    bevel: {
      top: "transparent",
      topBright: "transparent",
      topStrong: "transparent",
      bottom: "rgba(0, 0, 0, 0.2)",
      bottomDark: "rgba(0, 0, 0, 0.3)",
    },
  },

  material: {
    flat: true,
    blur: { lg: "none", md: "none", sm: "none" },
    shadow: {
      panel: NO_SHADOW,
      tile: NO_SHADOW,
      subtle: NO_SHADOW,
      sidebarEdge: "1px 0 0 rgba(236, 236, 236, 0.06)",
      sidebarCast: NO_SHADOW,
      // a faint white glow is the one allowed flourish, for emphasis
      accentHalo: "0 0 18px -6px rgba(255, 255, 255, 0.16)",
      accentHaloStrong: "0 0 22px -6px rgba(255, 255, 255, 0.24)",
    },
  },

  ambient: [
    // The top glow stays below the #212121 panel, so cards near the top
    // of a screen do not melt into the ground.
    "radial-gradient(ellipse 900px 600px at 50% -8%, rgba(255, 255, 255, 0.012), transparent)",
    "radial-gradient(ellipse 600px 600px at 8% 100%, rgba(255, 255, 255, 0.012), transparent)",
    "linear-gradient(180deg, #191919 0%, #171717 62%, #141414 100%)",
  ].join(", "),

  scales: {
    // Pure neutral grey ladder (no blue/warm tint).
    surface: {
      "50": "247 247 247",
      "100": "236 236 236",
      "200": "214 214 214",
      // 300-600 are TEXT greys (meta, placeholders): lifted to clear AA
      // (300 moves up to stay a step above 400).
      "300": "184 184 184",
      "400": "170 170 170",
      "500": "163 163 163",
      // 600 aliases 500 (see glass.ts).
      "600": "163 163 163",
      "700": "47 47 47",
      "800": "33 33 33",
      "880": "26 26 26",
      "900": "23 23 23",
      "950": "16 16 16",
    },
    // White accent scale — a neutral grey ladder. 500 = the soft-white
    // accent; 400 is the brightest (button hover); 800/900 are the dark
    // greys used for filled "accent" surfaces like the chat user bubble.
    accent: {
      "50": "250 250 250",
      "100": "240 240 240",
      "200": "224 224 224",
      "300": "206 206 206",
      "400": "255 255 255",
      "500": "236 236 236",
      "600": "208 208 208",
      "700": "140 140 140",
      "800": "64 64 64",
      "900": "47 47 47",
    },
  },

  // Monochrome decorative tail — the white accent + neutral greys. Tokens
  // that elsewhere reach for a "blue"/"violet"/"teal" cool accent map onto
  // light grey here so nothing reintroduces a colour cast.
  extended: {
    accentSoftRgb: "236, 236, 236",
    accentLightRgb: "255, 255, 255",
    accentInfo2Rgb: "163, 163, 163",
    accentSkyRgb: "200, 200, 200",
    accentGlowRgb: "236, 236, 236",
    accentGlowDeepRgb: "150, 150, 150",
    researchRgb: "200, 200, 200",
    catTealRgb: "150, 150, 150",
    accentInfo: "#cfcfcf",
    accentInfo2: "#a3a3a3",
    catViolet: "#cfcfcf",
    catSteel: "#a3a3a3",
    // cool-1 / cw-slate(-2) are used as TEXT: AA on bg, panel, tile and
    // raised (over the tile too). Ladder stays ordered dim -> bright.
    cool1: "#a3a3a3",
    cool2: "#adadad",
    cool3: "#b6b6b6",
    cool4: "#bdbdbd",
    cwSlate: "#a8a8a8",
    cwSlate2: "#a3a3a3",
    cwBlue: "#bdbdbd",
    cwBlueL: "#d4d4d4",
    cwBlueXl: "#e6e6e6",
    cwViolet: "#ececec",
    cwVioletL: "#ffffff",
    cwVioletD: "#cfcfcf",
    orgnodeRgb: "33, 33, 33",
    orgnodeSelRgb: "64, 64, 64",
    coolShadowRgb: "0, 0, 0",
    coolOverlayRgb: "23, 23, 23",
  },

  js: {
    canvasGrid: "#2f2f2f",
    canvasMask: "rgba(23, 23, 23, 0.8)",
    edgeStroke: "#8a8a8a",
    edgeRgb: "138, 138, 138",
    teamColours: [
      "rgba(236, 236, 236, 0.5)", // light
      "rgba(160, 160, 160, 0.45)", // mid grey
      "rgba(110, 110, 110, 0.45)", // dark grey
    ],
    // Vivid agent identity — the one place colour lives. Full colour
    // wheel (per-agent hue = hash % 360) so each agent gets its own
    // distinct, well-spread colour instead of clustering into a few
    // buckets; rendered brighter/more saturated than glass so the
    // otherwise-neutral roster/org chart stays lively rather than flat.
    avatarHues: "wheel",
    avatarBg: [58, 47], // vivid mid-tone chip (bright enough to read as the hue)
    avatarFg: [15, 96], // near-white initial for a clean, punchy contrast
  },
};
